// src/components/settings/MSMESettings.jsx

import React, { useEffect, useState } from "react";
import axiosInstance from "../../api/axiosInstance";

const MSMESettings = ({ initial, onSave }) => {
  const [form, setForm] = useState({
    msme_registration_number: "",
    msme_classification: "",
    msme_major_activity: "",
    msme_registration_date: "",
    msme_incorporation_date: "",
    msme_commencement_date: "",
    msme_nic_code: "",
    msme_nic_description: "",
    msme_status: "ACTIVE",
  });

  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (initial) {
      setForm({
        msme_registration_number:
          initial.msme_registration_number || "",
        msme_classification:
          initial.msme_classification || "",
        msme_major_activity:
          initial.msme_major_activity || "",
        msme_registration_date:
          initial.msme_registration_date
            ? initial.msme_registration_date.substring(0, 10)
            : "",
        msme_incorporation_date:
          initial.msme_incorporation_date
            ? initial.msme_incorporation_date.substring(0, 10)
            : "",
        msme_commencement_date:
          initial.msme_commencement_date
            ? initial.msme_commencement_date.substring(0, 10)
            : "",
        msme_nic_code:
          initial.msme_nic_code || "",
        msme_nic_description:
          initial.msme_nic_description || "",
        msme_status:
          initial.msme_status || "ACTIVE",
      });
    }
  }, [initial]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    setMessage("");
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage("");

      if (
        form.msme_registration_number &&
        !/^UDYAM-[A-Z]{2}-\d{2}-\d{7}$/.test(
          form.msme_registration_number.toUpperCase()
        )
      ) {
        setMessage(
          "Please enter a valid Udyam Registration Number."
        );
        setSaving(false);
        return;
      }

      const payload = {
        ...form,
        msme_registration_number:
          form.msme_registration_number
            .trim()
            .toUpperCase(),
        msme_nic_code:
          form.msme_nic_code.trim(),
        msme_nic_description:
          form.msme_nic_description.trim(),
      };

      await onSave(payload);

      setMessage("MSME settings saved successfully.");
    } catch (error) {
      console.error("Error saving MSME settings:", error);

      setMessage(
        error?.response?.data?.error ||
          "Failed to save MSME settings."
      );
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    "w-full h-9 px-3 text-sm border border-gray-300 rounded-md " +
    "bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 " +
    "focus:border-blue-500";

  const labelClass =
    "block text-xs font-medium text-gray-600 mb-1";

  return (
    <div className="w-full">

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-gray-800">
            MSME / Udyam Settings
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Maintain your company's MSME / Udyam registration details.
          </p>
        </div>
      </div>

      {/* Registration Details */}
      <div className="border border-gray-200 rounded-lg bg-white mb-4">
        <div className="px-4 py-2.5 border-b border-gray-200 bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-700">
            Udyam Registration Details
          </h3>
        </div>

        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">

          {/* Udyam Number */}
          <div>
            <label className={labelClass}>
              Udyam Registration Number
            </label>

            <input
              type="text"
              name="msme_registration_number"
              value={form.msme_registration_number}
              onChange={handleChange}
              placeholder="UDYAM-KA-00-0000000"
              className={inputClass}
              maxLength={19}
            />

            <p className="text-[11px] text-gray-400 mt-1">
              Example: UDYAM-KA-00-0000000
            </p>
          </div>

          {/* Classification */}
          <div>
            <label className={labelClass}>
              MSME Classification
            </label>

            <select
              name="msme_classification"
              value={form.msme_classification}
              onChange={handleChange}
              className={inputClass}
            >
              <option value="">Select Classification</option>
              <option value="MICRO">Micro</option>
              <option value="SMALL">Small</option>
              <option value="MEDIUM">Medium</option>
            </select>
          </div>

          {/* Major Activity */}
          <div>
            <label className={labelClass}>
              Major Activity
            </label>

            <select
              name="msme_major_activity"
              value={form.msme_major_activity}
              onChange={handleChange}
              className={inputClass}
            >
              <option value="">Select Activity</option>
              <option value="MANUFACTURING">
                Manufacturing
              </option>
              <option value="SERVICES">
                Services
              </option>
              <option value="TRADING">
                Trading
              </option>
            </select>
          </div>

          {/* Status */}
          <div>
            <label className={labelClass}>
              MSME Status
            </label>

            <select
              name="msme_status"
              value={form.msme_status}
              onChange={handleChange}
              className={inputClass}
            >
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
            </select>
          </div>

        </div>
      </div>

      {/* Dates */}
      <div className="border border-gray-200 rounded-lg bg-white mb-4">
        <div className="px-4 py-2.5 border-b border-gray-200 bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-700">
            Registration Dates
          </h3>
        </div>

        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">

          <div>
            <label className={labelClass}>
              Incorporation / Registration Date
            </label>

            <input
              type="date"
              name="msme_incorporation_date"
              value={form.msme_incorporation_date}
              onChange={handleChange}
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>
              Commencement of Business Date
            </label>

            <input
              type="date"
              name="msme_commencement_date"
              value={form.msme_commencement_date}
              onChange={handleChange}
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>
              Udyam Registration Date
            </label>

            <input
              type="date"
              name="msme_registration_date"
              value={form.msme_registration_date}
              onChange={handleChange}
              className={inputClass}
            />
          </div>

        </div>
      </div>

      {/* Business Activity */}
      <div className="border border-gray-200 rounded-lg bg-white mb-4">
        <div className="px-4 py-2.5 border-b border-gray-200 bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-700">
            Business Activity
          </h3>
        </div>

        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">

          <div>
            <label className={labelClass}>
              Primary NIC Code
            </label>

            <input
              type="text"
              name="msme_nic_code"
              value={form.msme_nic_code}
              onChange={handleChange}
              placeholder="Enter NIC code"
              className={inputClass}
              maxLength={20}
            />
          </div>

          <div>
            <label className={labelClass}>
              NIC Activity Description
            </label>

            <input
              type="text"
              name="msme_nic_description"
              value={form.msme_nic_description}
              onChange={handleChange}
              placeholder="Enter activity description"
              className={inputClass}
              maxLength={255}
            />
          </div>

        </div>
      </div>

      {/* Official Portal */}
      <div className="border border-blue-100 rounded-lg bg-blue-50 mb-4">
        <div className="p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
          <div>
            <div className="text-sm font-semibold text-gray-700">
              Udyam Registration
            </div>
            <div className="text-xs text-gray-500">
              Register or verify your MSME details on the official portal.
            </div>
          </div>

          <a
            href="https://udyamregistration.gov.in/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center h-8 px-3
                       text-xs font-medium text-blue-700
                       border border-blue-200 rounded-md bg-white
                       hover:bg-blue-100 transition"
          >
            Open Udyam Portal
          </a>
        </div>
      </div>

      {/* Bottom Actions */}
      <div className="flex items-center justify-between">

        <div>
          {message && (
            <span
              className={`text-xs ${
                message.toLowerCase().includes("success")
                  ? "text-green-600"
                  : "text-red-600"
              }`}
            >
              {message}
            </span>
          )}
        </div>

        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="h-9 px-5 text-sm font-medium text-white
                     bg-blue-600 rounded-md hover:bg-blue-700
                     disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {saving ? "Saving..." : "Save Changes"}
        </button>

      </div>

    </div>
  );
};

export default MSMESettings;